<template>
  <BaseDialog contentClass="payload-preview-dialog" ref="dialog" :show-actions="false" @closeModal="closeDialog">
    <template slot="title">{{ $t('page.admin-sync-profiles.payload-preview.title') }}</template>
    <template slot="content">
      <!-- Scope tabs -->
      <div class="payload-preview-dialog__scope-tabs">
        <button
          class="payload-preview-dialog__scope-tab"
          :class="{ 'payload-preview-dialog__scope-tab--active': scope === 'opportunity' }"
          @click="scope = 'opportunity'"
        >
          {{ $t('page.admin-sync-profiles.scope-filter.opportunity') }}
        </button>
        <button
          class="payload-preview-dialog__scope-tab"
          :class="{ 'payload-preview-dialog__scope-tab--active': scope === 'quote' }"
          @click="scope = 'quote'"
        >
          {{ $t('page.admin-sync-profiles.scope-filter.quote') }}
        </button>
      </div>

      <!-- Optional opportunity picker — when set, the JSON values are resolved against it. -->
      <div class="payload-preview-dialog__picker-row">
        <div class="payload-preview-dialog__picker">
          <input
            ref="search"
            type="text"
            class="payload-preview-dialog__search"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="dropdownOpen ? 'true' : 'false'"
            :aria-controls="listboxId"
            :aria-activedescendant="activeDescendantId || null"
            v-model="searchQuery"
            :placeholder="
              selectedOpportunity
                ? selectedOpportunity.name
                : $t('page.admin-sync-profiles.payload-preview.picker-placeholder')
            "
            @input="onSearchInput"
            @focus="onSearchFocus"
            @click="onSearchFocus"
            @blur="onSearchBlur"
            @keydown="onSearchKeydown"
          />
          <div
            v-if="dropdownOpen"
            :id="listboxId"
            role="listbox"
            class="payload-preview-dialog__dropdown"
            :style="dropdownStyle"
            @mousedown.prevent
          >
            <div v-if="searching" class="payload-preview-dialog__dropdown-empty">
              {{ $t('page.admin-sync-profiles.test-panel.searching') }}
            </div>
            <div v-else-if="results.length === 0" class="payload-preview-dialog__dropdown-empty">
              {{ $t('page.admin-sync-profiles.test-panel.no-results') }}
            </div>
            <div
              v-for="(opp, idx) in results"
              :key="opp.id"
              :id="optionId(opp)"
              role="option"
              :aria-selected="idx === activeIndex ? 'true' : 'false'"
              :class="[
                'payload-preview-dialog__option',
                {
                  'payload-preview-dialog__option--current': isCurrentOpportunity(opp),
                  'payload-preview-dialog__option--active': idx === activeIndex,
                },
              ]"
              @mouseenter="activeIndex = idx"
              @click="selectOpportunity(opp)"
            >
              <span class="payload-preview-dialog__option-name">{{ opp.name }}</span>
              <span v-if="isCurrentOpportunity(opp)" class="payload-preview-dialog__option-check" aria-hidden="true"
                >✓</span
              >
            </div>
          </div>
        </div>
        <button
          v-if="selectedOpportunity"
          type="button"
          class="payload-preview-dialog__clear-button"
          :title="$t('page.admin-sync-profiles.payload-preview.clear-opportunity')"
          @click="clearOpportunity"
        >
          ×
        </button>
      </div>
      <p v-if="selectedOpportunity" class="payload-preview-dialog__resolved-note">
        {{ $t('page.admin-sync-profiles.payload-preview.resolved-against', { name: selectedOpportunity.name }) }}
      </p>
      <p v-else class="payload-preview-dialog__template-note">
        {{ $t('page.admin-sync-profiles.payload-preview.template-note') }}
      </p>

      <!-- HTTP-level failure banner (covers 400 / 404 / 403 / network). -->
      <div v-if="previewError" class="payload-preview-dialog__error-banner">
        {{ previewError }}
      </div>

      <!-- Code block -->
      <div class="payload-preview-dialog__code-wrap">
        <button class="payload-preview-dialog__copy-btn" :disabled="!previewJson" @click="copyToClipboard">
          {{
            copied
              ? $t('page.admin-sync-profiles.payload-preview.copied')
              : $t('page.admin-sync-profiles.payload-preview.copy')
          }}
        </button>
        <div v-if="resolving" class="payload-preview-dialog__loading">
          <span class="payload-preview-dialog__spinner" aria-hidden="true" />
          {{ $t('page.admin-sync-profiles.payload-preview.resolving') }}
        </div>
        <pre v-else-if="previewJson" class="payload-preview-dialog__code">{{ previewJson }}</pre>
        <div v-else class="payload-preview-dialog__empty">
          {{ $t('page.admin-sync-profiles.payload-preview.empty') }}
        </div>
      </div>

      <!-- Diagnostics — only shown in resolved mode, only rows that didn't render as a normal value. -->
      <section v-if="selectedOpportunity && diagnostics.length > 0" class="payload-preview-dialog__diagnostics">
        <div class="payload-preview-dialog__diagnostics-title">
          {{ $t('page.admin-sync-profiles.payload-preview.diagnostics-title') }}
        </div>
        <table class="payload-preview-dialog__diag-table">
          <tbody>
            <tr v-for="d in diagnostics" :key="d.id">
              <td>
                <code class="payload-preview-dialog__diag-field">{{ d.crmField }}</code>
              </td>
              <td>
                <code class="payload-preview-dialog__diag-expression" :title="d.valueExpression">{{
                  d.valueExpression
                }}</code>
              </td>
              <td>
                <span :class="['payload-preview-dialog__diag-label', `payload-preview-dialog__diag-label--${d.kind}`]">
                  {{ d.label }}
                </span>
              </td>
              <td class="payload-preview-dialog__diag-message" :title="d.message || ''">{{ d.message }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </BaseDialog>
</template>

<script>
import axios from 'axios';
import BaseDialog from '@/components/base/BaseDialog';
import { mapGetters } from 'vuex';
import { opportunityService } from '@/services/opportunity.service';
import { opportunitySyncProfilesService } from '@/services/opportunity-sync-profiles.service';

const STORAGE_PREFIX = 'value-expression-test:opportunity:';

export default {
  name: 'PayloadPreviewDialog',
  components: { BaseDialog },
  props: {
    // The profile context drives the per-(user, profile) opportunity-picker memory.
    profileId: { type: String, default: null },
  },
  data() {
    return {
      // View state
      scope: 'opportunity',
      copied: false,

      // Picker state
      searchQuery: '',
      searchFocused: false,
      results: [],
      searching: false,
      // Keyboard-highlighted option index; resets on results change and on mouseenter.
      activeIndex: 0,
      selectedOpportunity: null,
      dropdownStyle: { display: 'none' },

      // Resolve state — `responses[mappingId]` holds the batch-preview result per mapping.
      resolving: false,
      responses: {},
      previewError: null,
    };
  },
  computed: {
    ...mapGetters('adminSyncProfileFieldMappings', ['fieldMappings']),
    ...mapGetters('auth', ['currentUser']),
    storageKey() {
      const userId = this.currentUser && this.currentUser.id ? this.currentUser.id : 'anon';
      const suffix = this.profileId ? `:${this.profileId}` : '';
      return `${STORAGE_PREFIX}${userId}${suffix}`;
    },
    dropdownOpen() {
      return this.searchFocused && (this.searching || this.results.length > 0 || this.searchQuery.length > 0);
    },
    // Per-instance ARIA IDs — `this._uid` keeps them unique if multiple dialogs ever mount.
    listboxId() {
      return `payload-preview-listbox-${this._uid}`;
    },
    activeDescendantId() {
      if (!this.dropdownOpen) return null;
      const opp = this.results[this.activeIndex];
      return opp ? this.optionId(opp) : null;
    },
    scopedMappings() {
      return this.fieldMappings
        .filter((m) => !m.entityScope || m.entityScope.includes(this.scope))
        .slice()
        .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    },
    // Build the JSON shown in the code block.
    // - No opportunity → static template (existing behavior; placeholders preserved verbatim).
    // - With opportunity → resolved values; rows that would be omitted are skipped from the
    //   payload (matching what the back-end actually does at sync time); parse errors render
    //   as `null` and are flagged in the diagnostics footer below.
    previewJson() {
      if (this.scopedMappings.length === 0) return null;
      const resolved = !!this.selectedOpportunity && !this.resolving;
      const payload = {};
      for (const m of this.scopedMappings) {
        if (resolved) {
          const r = this.responses[m.id];
          if (!r) {
            // Not in cache (shouldn't happen post-fetch, but be safe). Fall back to template render.
            payload[m.crmFieldName] = this.renderTemplateValue(m);
            continue;
          }
          if (r.wouldOmit) {
            // Skip — back-end drops this key. Diagnostics footer surfaces it.
            continue;
          }
          if (r.parseError) {
            // Emit null and rely on diagnostics for the error message.
            payload[m.crmFieldName] = null;
            continue;
          }
          payload[m.crmFieldName] = this.coerceResolvedValue(r);
        } else {
          payload[m.crmFieldName] = this.renderTemplateValue(m);
        }
      }
      return JSON.stringify(payload, null, 2);
    },
    diagnostics() {
      if (!this.selectedOpportunity || this.resolving) return [];
      const omitLabel = this.$t('page.admin-sync-profiles.payload-preview.diagnostics-omit-label');
      const errorLabel = this.$t('page.admin-sync-profiles.payload-preview.diagnostics-error-label');
      const rows = [];
      for (const m of this.scopedMappings) {
        const r = this.responses[m.id];
        if (!r) continue;
        const base = { id: m.id, crmField: m.crmFieldName, valueExpression: m.valueExpression || '' };
        if (r.wouldOmit) {
          rows.push({ ...base, kind: 'omit', label: omitLabel, message: '' });
        } else if (r.parseError) {
          rows.push({ ...base, kind: 'error', label: errorLabel, message: r.parseError });
        }
      }
      return rows;
    },
  },
  watch: {
    selectedOpportunity(opp) {
      if (opp) {
        this.resolveAll();
      } else {
        this.responses = {};
        this.previewError = null;
      }
    },
    fieldMappings() {
      // Profile reloaded or mapping list mutated — invalidate cached responses so the next
      // resolve is fresh. If an opportunity is selected, re-fetch.
      this.responses = {};
      if (this.selectedOpportunity) this.resolveAll();
    },
    dropdownOpen(visible) {
      if (visible) this.$nextTick(() => this.positionDropdown());
    },
    results() {
      // New result set — reset the keyboard cursor to the top.
      this.activeIndex = 0;
      if (this.dropdownOpen) this.$nextTick(() => this.positionDropdown());
    },
  },
  methods: {
    openDialog() {
      this.scope = 'opportunity';
      this.copied = false;
      this.searchQuery = '';
      this.results = [];
      this.searchFocused = false;
      this.previewError = null;
      this.restoreLastOpportunity();
      this.$refs.dialog.openModal();
      if (!this._reflowAttached) {
        this._onReflow = () => {
          if (this.dropdownOpen) this.positionDropdown();
        };
        window.addEventListener('resize', this._onReflow);
        window.addEventListener('scroll', this._onReflow, true);
        this._reflowAttached = true;
      }
    },
    closeDialog() {
      clearTimeout(this._searchTimer);
      if (this._searchCancelSource) this._searchCancelSource.cancel('Dialog closed');
      if (this._reflowAttached) {
        window.removeEventListener('resize', this._onReflow);
        window.removeEventListener('scroll', this._onReflow, true);
        this._reflowAttached = false;
      }
      this.$refs.dialog.isModalOpen = false;
    },
    // ── Picker ───────────────────────────────────────────────────────────────────
    onSearchFocus() {
      this.searchFocused = true;
      // Skip if a fetch is already in flight — clicking an unfocused input fires both
      // `focus` and `click`, which would otherwise run runSearch() twice (the in-flight
      // one hasn't populated `results` yet, so the second call passes the empty-guard).
      if (this.results.length === 0 && !this.searching) this.runSearch();
      this.$nextTick(() => this.positionDropdown());
    },
    onSearchBlur() {
      setTimeout(() => {
        this.searchFocused = false;
      }, 120);
    },
    onSearchInput() {
      // Typing should always reopen the dropdown — after a selectOpportunity() we cleared
      // searchFocused to hide the picker, but the input never lost real focus, so the next
      // `focus` event won't fire. Force it back true on any keystroke.
      this.searchFocused = true;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.runSearch(), 200);
      this.$nextTick(() => this.positionDropdown());
    },
    onSearchKeydown(event) {
      // Escape always closes the dropdown, even when there are no results to navigate.
      if (event.key === 'Escape') {
        event.preventDefault();
        this.searchFocused = false;
        return;
      }
      if (!this.dropdownOpen || this.results.length === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.results.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'Enter': {
          event.preventDefault();
          const opp = this.results[this.activeIndex];
          if (opp) this.selectOpportunity(opp);
          break;
        }
      }
    },
    scrollActiveIntoView() {
      const active = this.$el && this.$el.querySelector('.payload-preview-dialog__option--active');
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    },
    optionId(opp) {
      return `payload-preview-option-${this._uid}-${opp.id}`;
    },
    runSearch() {
      const q = (this.searchQuery || '').trim();
      // Per-instance cancel source so picker requests don't stomp on other components'
      // in-flight `getOpportunities` calls (e.g. the main Opportunities list page).
      // Cancel the dialog's own previous request first; the seq guard below is the
      // belt-and-suspenders catch for any response that slipped through before cancel.
      if (this._searchCancelSource) this._searchCancelSource.cancel('Newer search');
      this._searchCancelSource = axios.CancelToken.source();
      this._searchSeq = (this._searchSeq || 0) + 1;
      const seq = this._searchSeq;
      this.searching = true;
      opportunityService
        .getOpportunities({
          page: 1,
          pageSize: 5,
          sortingField: 'created_at',
          sortingOrder: 'desc',
          filters: q ? { query: q } : {},
          cancelToken: this._searchCancelSource,
        })
        .then(({ opportunities }) => {
          if (seq !== this._searchSeq) return;
          // Server already capped at 5 via pageSize.
          this.results = opportunities || [];
        })
        .catch(() => {
          if (seq !== this._searchSeq) return;
          this.results = [];
        })
        .finally(() => {
          if (seq !== this._searchSeq) return;
          this.searching = false;
        });
    },
    isCurrentOpportunity(opp) {
      return !!this.selectedOpportunity && opp.id === this.selectedOpportunity.id;
    },
    selectOpportunity(opp) {
      this.selectedOpportunity = { id: opp.id, name: opp.name };
      this.searchQuery = '';
      this.results = [];
      this.searchFocused = false;
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({ id: opp.id, name: opp.name }));
      } catch {
        // non-fatal
      }
    },
    clearOpportunity() {
      this.selectedOpportunity = null;
      // Clear localStorage too — the user explicitly wants to leave resolved mode.
      try {
        localStorage.removeItem(this.storageKey);
      } catch {
        // non-fatal
      }
    },
    restoreLastOpportunity() {
      let stored;
      try {
        const raw = localStorage.getItem(this.storageKey);
        stored = raw ? JSON.parse(raw) : null;
      } catch {
        stored = null;
      }
      if (!stored || !stored.id) {
        this.selectedOpportunity = null;
        return;
      }
      this.selectedOpportunity = { id: stored.id, name: stored.name };
      // Refresh name in background; a deleted/inaccessible opportunity will surface via
      // resolveAll's catch.
      opportunityService
        .getOpportunity({ id: stored.id })
        .then((opp) => {
          if (opp && opp.id === stored.id) {
            this.selectedOpportunity = { id: opp.id, name: opp.name };
          }
        })
        .catch(() => {});
    },
    positionDropdown() {
      const el = this.$refs.search;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const padding = 8;
      const preferredMax = 240;
      const spaceBelow = Math.max(0, viewportH - rect.bottom - padding);
      const spaceAbove = Math.max(0, rect.top - padding);
      const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
      const maxHeight = Math.min(preferredMax, openDown ? spaceBelow : spaceAbove);
      this.dropdownStyle = openDown
        ? {
            position: 'fixed',
            top: `${rect.bottom + 2}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            maxHeight: `${maxHeight}px`,
          }
        : {
            position: 'fixed',
            bottom: `${viewportH - rect.top + 2}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            maxHeight: `${maxHeight}px`,
          };
    },
    // ── Resolve ──────────────────────────────────────────────────────────────────
    resolveAll() {
      if (!this.selectedOpportunity || this.fieldMappings.length === 0) return;
      this.resolving = true;
      this.previewError = null;
      // Send EVERY mapping (not just the current scope) so flipping the scope tab is
      // instantaneous on cached responses. The endpoint caps at 100; current profiles are
      // far below that.
      const all = this.fieldMappings;
      const valueExpressions = all.map((m) => m.valueExpression || '');
      opportunitySyncProfilesService
        .previewValueExpressionBatch({
          opportunityId: this.selectedOpportunity.id,
          valueExpressions,
        })
        .then((batch) => {
          if (!Array.isArray(batch) || batch.length !== all.length) {
            this.previewError = this.$t('page.admin-sync-profiles.test-panel.unknown-error');
            this.responses = {};
            return;
          }
          const next = {};
          all.forEach((m, i) => {
            next[m.id] = batch[i];
          });
          this.responses = next;
        })
        .catch((err) => {
          const status = err && err.response && err.response.status;
          let msg;
          if (status === 400) {
            msg = this.$t('page.admin-sync-profiles.test-panel.invalid-request');
          } else if (status === 404) {
            msg = this.$t('page.admin-sync-profiles.test-panel.opportunity-not-found');
          } else if (status === 403) {
            msg = this.$t('page.admin-sync-profiles.test-panel.not-allowed');
          } else {
            msg =
              (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
              this.$t('page.admin-sync-profiles.test-panel.unknown-error');
          }
          this.previewError = msg;
          this.responses = {};
        })
        .finally(() => {
          this.resolving = false;
        });
    },
    // ── Rendering helpers ────────────────────────────────────────────────────────
    renderTemplateValue(m) {
      // Template view (unresolved). Number fields with a bare numeric literal emit unquoted;
      // everything else emits the raw `valueExpression` string with placeholders intact.
      const v = m.valueExpression == null ? '' : String(m.valueExpression);
      const trimmed = v.trim();
      const isTemplate = /\{\{[\s\S]*\}\}/.test(trimmed);
      if (m.fieldType === 'number' && !isTemplate && trimmed !== '' && !isNaN(Number(trimmed))) {
        return Number(trimmed);
      }
      return v;
    },
    coerceResolvedValue(r) {
      // The back-end returns `result` as a string regardless of the underlying type, but tells
      // us the native type via `resultType`. Coerce so the JSON shows the right shape.
      const t = r.resultType;
      const raw = r.result;
      if (raw == null) return null;
      if (t === 'NUMBER') {
        const n = Number(raw);
        return isNaN(n) ? raw : n;
      }
      if (t === 'BOOLEAN') {
        return raw === 'true';
      }
      if (t === 'NULL') {
        return null;
      }
      if (t === 'OBJECT' || t === 'ARRAY') {
        // Try to parse — back-end already serialized JSON; if it fails, fall back to string.
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
      }
      // STRING (default)
      return raw;
    },
    // ── Copy ─────────────────────────────────────────────────────────────────────
    async copyToClipboard() {
      if (!this.previewJson) return;
      try {
        await navigator.clipboard.writeText(this.previewJson);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch {
        // Clipboard may be blocked (insecure context); silently ignore.
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__payload-preview-dialog {
  height: auto;
  width: 720px;

  .base-dialog__content {
    padding: 28px 32px;
    background: $color-white !important;
  }
}

.payload-preview-dialog {
  &__scope-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 16px;
  }

  &__scope-tab {
    padding: 4px 16px;
    border-radius: 20px;
    border: 1px solid $color-border;
    background: $color-white;
    color: $color-normal-pale;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease, border-color 150ms ease;

    &:hover {
      color: $color-accent;
      border-color: $color-accent;
    }

    &--active {
      background: $color-accent;
      border-color: $color-accent;
      color: $color-white;

      &:hover {
        color: $color-white;
      }
    }
  }

  &__picker-row {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 6px;
  }

  &__picker {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__search {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border: 1px solid $color-border;
    border-radius: 4px;
    font-size: 13px;
    background: $color-neutral-pale;
    color: $color-normal;

    &:focus {
      outline: none;
      border-color: $color-accent;
      background: $color-white;
    }
  }

  &__clear-button {
    flex: 0 0 auto;
    height: 36px;
    width: 36px;
    border: 1px solid $color-border;
    border-radius: 4px;
    background: $color-white;
    color: $color-normal-pale;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover {
      color: $color-error;
      border-color: $color-error;
    }
  }

  &__dropdown {
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: 6px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    z-index: 2000;
    overflow-y: auto;
  }

  &__dropdown-empty {
    padding: 8px 12px;
    color: $color-normal-pale;
    font-size: 13px;
    font-style: italic;
  }

  &__option {
    padding: 6px 12px;
    cursor: pointer;
    font-size: 13px;
    color: $color-normal;
    display: flex;
    align-items: center;
    gap: 8px;

    // Mouse hover and keyboard-active share the same highlight, so navigation modes agree.
    &:hover,
    &--active {
      background: $color-neutral-pale;
    }

    &--current {
      background: $color-accent-100;
      font-weight: 600;

      &:hover,
      &.payload-preview-dialog__option--active {
        background: $color-accent-200;
      }
    }
  }

  &__option-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__option-check {
    color: $color-accent;
    font-weight: 700;
    flex: 0 0 auto;
  }

  &__resolved-note,
  &__template-note {
    font-size: 12px;
    color: $color-normal-pale;
    margin: 4px 0 14px;
    line-height: 1.4;
  }

  &__resolved-note {
    color: $color-normal;
  }

  &__error-banner {
    background: $color-error-pale;
    border: 1px solid rgba($color-error, 0.4);
    color: $color-error;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 13px;
    margin-bottom: 12px;
  }

  &__code-wrap {
    position: relative;
    background: $color-neutral-pale;
    border: 1px solid $color-border;
    border-radius: 6px;
    overflow: hidden;
    min-height: 60px;
  }

  &__copy-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 3px 10px;
    border-radius: 4px;
    border: 1px solid $color-border;
    background: $color-white;
    color: $color-normal-pale;
    font-size: 12px;
    cursor: pointer;
    transition: color 150ms ease, border-color 150ms ease;
    z-index: 1;

    &:hover:not(:disabled) {
      color: $color-accent;
      border-color: $color-accent;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__loading {
    padding: 18px;
    text-align: center;
    color: $color-normal-pale;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba($color-normal-pale, 0.4);
    border-top-color: $color-accent;
    border-radius: 50%;
    animation: payload-preview-spin 0.7s linear infinite;
  }

  &__code {
    margin: 0;
    padding: 16px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 13px;
    line-height: 1.6;
    color: $color-normal;
    white-space: pre;
    overflow: auto;
    max-height: 50vh;
  }

  &__empty {
    padding: 24px;
    text-align: center;
    color: $color-normal-pale;
    font-size: 14px;
    font-style: italic;
  }

  &__diagnostics {
    margin-top: 16px;
    border-top: 1px solid $color-border;
    padding-top: 12px;
  }

  &__diagnostics-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-normal-pale;
    margin-bottom: 8px;
  }

  &__diag-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;

    td {
      padding: 4px 8px 4px 0;
      vertical-align: top;
    }

    td:first-child {
      white-space: nowrap;
      width: 1%;
    }
  }

  &__diag-field {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    color: $color-normal;
  }

  &__diag-expression {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    color: $color-normal-pale;
    display: inline-block;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__diag-label {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 3px;

    &--omit {
      color: $color-yellow;
      background: $color-yellow-pale;
    }
    &--error {
      color: $color-error;
      background: $color-error-pale;
    }
  }

  &__diag-message {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    color: $color-normal-pale;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 360px;
  }
}

@keyframes payload-preview-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
